/**
 * Shared layout math for RICHTEXT card objects, used by BOTH the renderer
 * (`RichTextCardObject`) and the editor (`RichTextCardObjectEditor`) so the two
 * cannot drift — the WYSIWYG guarantee. See the text-rendering & typography
 * LLD §6.1 (Phase 0 / issue #574).
 */

/**
 * Base size for rich text with no inline `font-size` of its own.
 *
 * Both the editor and the renderer must use this, because text wraps on the
 * ratio BOX / FONT and a 1px difference is enough to move a word. Worked
 * example, from the repro that found it — a 54%-wide box at scale 246:
 *
 *     box ÷ 15px = 15.8 characters per line
 *     box ÷ 16px = 14.8 characters per line   ← one character fewer
 *
 * One character is a whole word at the wrong moment: the video broke after
 * "for" where the editor broke after "your" (Laura, 2026-10-13).
 *
 * 16 rather than 15 because two of the three surfaces that draw this text
 * already used 16 — `/view`, which is what recipients see, and the Remotion
 * preview, which is what artists tune against. Only the edit box said 15, so
 * it is the one that moved. Choosing 15 instead would have re-rendered text on
 * cards recipients had already opened; see commit 0300164f and the census
 * scans in PR #785 for the counts.
 *
 * ⚠️ Restated in three places that cannot import it: the
 * `.ContentEditable__root` rule and the ToolbarPlugin's two fallbacks.
 * `richTextLayout.scale.test.ts` reads all three and asserts they match this,
 * so changing this alone fails the suite instead of silently reintroducing the
 * drift.
 */
export const RICHTEXT_DEFAULT_FONT_PX = 16;

/**
 * A scale of 100 means "drawn at its authored size", so it divides out of
 * `resolveRichTextWidthPx` and leaves the plain `width% x cardWidth` box.
 *
 * Both callers need it and for different reasons — the editor when an object
 * has no scale stored, the renderer for wrap-off objects whose geometry must
 * not move — so it lives here rather than as a literal in each.
 */
export const NO_SCALE = 100;

/**
 * The box a wrap-ON object uses when it has no authored width.
 *
 * Supplied at READ time, never written into the object. Enabling wrap used to
 * inject `width = 40` so a box would exist to wrap within, which left any
 * object that had none — most of the stored corpus, which predates the factory
 * supplying a width — permanently altered. Turning wrap back off kept the
 * injected value, and the width control was hidden with wrap off, so nothing
 * could undo it (Laura, 2026-10-15).
 *
 * Reading the default instead means enabling wrap mutates nothing and disabling
 * it returns the object exactly to where it started. The cost is that BOTH
 * render surfaces must apply it, or a box appears on one and not the other —
 * guarded by richTextLayout.scale.test.ts.
 *
 * Wrap-OFF deliberately does NOT default: there, no width means an auto box
 * that sizes to its content, which is what the legacy corpus renders today.
 */
export const WRAP_DEFAULT_WIDTH_PCT = 40;

/**
 * Resolve the RICHTEXT box width, in LAYOUT px, from the stored `width`
 * percentage, the card's width, and the object's own scale.
 *
 * A falsy `widthPercent` (`undefined` or `0`), a missing card width, or a
 * non-positive scale all mean "no width constraint".
 *
 * ## Why scale is a parameter and not an afterthought
 *
 * Wrapping depends only on the ratio between the box and the font. The object
 * is drawn with CSS `scale:` (see `useCurrentFrameStyle`), and `scale`, like
 * `transform`, is applied AFTER layout — so the browser wraps the text at the
 * UNSCALED font size, then magnifies the finished result.
 *
 * That means a box expressed in final on-card px is the wrong box to lay out
 * in. To end up occupying `widthPercent%` of the card after a `scale/100`
 * magnification, the text must be laid out in a box that fraction SMALLER:
 *
 *     layoutWidth x (scale / 100) === (widthPercent / 100) x cardWidthPx
 *
 * Omitting this is what broke staging on 2026-10-01. PR #762 moved the
 * renderer's `cardWidthPx` from the measured on-screen width into composition
 * space — correct for `/view`, where the measured width was 0 before layout
 * settled — but the font did not move with it and scale was never considered.
 * At width 40%, composition 1080 and scale 500, the box came out 432px against
 * ~330px of 15px text: it fitted on one line, never wrapped, and was then
 * magnified 5x into ~1650px of text across a 1080px card.
 *
 * `scalePercent` is REQUIRED rather than defaulted. The previous optional
 * `allowBleed` parameter was added, unit-tested, and then never passed at the
 * call site; a required parameter makes that omission a compile error instead
 * of a silent wrong answer.
 *
 * Callers must pass the object's RESTING scale (`cardObject.transform.scale`),
 * never the animated per-frame value — otherwise text re-flows mid-animation.
 * Animating a text box must move it, not re-wrap its words.
 */
export function resolveRichTextWidthPx(
  widthPercent: number | undefined,
  cardWidthPx: number | undefined,
  scalePercent: number,
): number | undefined {
  if (!widthPercent || !cardWidthPx || scalePercent <= 0) return undefined;
  const finalWidthPx = (widthPercent / 100) * cardWidthPx;
  return finalWidthPx / (scalePercent / 100);
}

/**
 * Wrapping is OPT-IN per object via an explicit `wrap` flag (default off).
 * Every existing object has no flag → single-line `nowrap`, exactly as today,
 * so nothing existing or already-sent changes. Only objects an artist turns on
 * will wrap (within their width box). This is the minimal, safe seed of the
 * LLD's future `layoutMode`.
 */
export function richTextWhiteSpace(
  wrap: boolean | undefined,
): "normal" | "nowrap" {
  return wrap ? "normal" : "nowrap";
}
